import { useEffect, useRef, useState } from 'react';

import { PageHeader } from '@/components/app-shell/PageHeader';
import { fetchTenants, type TenantCard } from '@/features/admin/admin-api';
import { useAuthStore } from '@/features/auth/auth-store';

import { REPORT_TYPES, uploadIngestionFile } from './ingestion-api';

/**
 * Data Ingestion page (SCRUM-677) — the deposit half of INGESTION-FRAMEWORK.md §2. A CSV extract
 * is uploaded DIRECTLY to the tenant's COS landing bucket via a short-lived presigned PUT URL
 * (see ingestion-api.ts); the poller then enqueues an IngestionJob on its next cycle, so the file
 * is processed asynchronously (not instantly) — messaged below the dropzone.
 *
 * Two caller shapes share the page (admin-only surface as of SCRUM-984):
 *   - Bank uploader (Bank Admin): deposits into their OWN tenant (from the JWT claim); no client
 *     selector is shown. (Bank Executive is NOT an ingestion role — SCRUM-984.)
 *   - Platform Admin: must first pick an ACTIVE client bank from the selector, then the upload is
 *     scoped to that tenant (server-re-validated).
 *
 * Server authorization (module:Ingestion — PlatformAdmin + BankAdmin) is the real gate; this
 * page's visibility is nav parity, and the route guard redirects any other role away.
 */
export function IngestionPage() {
  const user = useAuthStore((s) => s.user);
  const isPlatformAdmin = user?.role === 'PlatformAdmin' || user?.role === 'Platform Admin';

  const [activeTenants, setActiveTenants] = useState<TenantCard[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [tenantsError, setTenantsError] = useState<string | null>(null);
  const [reportType, setReportType] = useState('');

  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState<{ name: string; key: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Platform Admin: load the Active client banks for the selector. A bank uploader never calls
  // this (the admin tenants endpoint is PA-gated) — guarded by isPlatformAdmin.
  useEffect(() => {
    if (!isPlatformAdmin) return;
    let cancelled = false;
    fetchTenants()
      .then((tenants) => {
        if (cancelled) return;
        setActiveTenants(tenants.filter((t) => t.status === 'active'));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setTenantsError(e instanceof Error ? e.message : 'Could not load client banks.');
      });
    return () => {
      cancelled = true;
    };
  }, [isPlatformAdmin]);

  // A Platform Admin must select a target tenant before uploading; a bank uploader is always ready.
  const targetReady = !isPlatformAdmin || selectedTenantId.length > 0;

  const handleFiles = (list: FileList | File[]) => {
    const files = Array.from(list);
    if (files.length === 0) return;

    const file = files[0];
    if (!file) return;

    // CSV-only (client guard; the server also enforces .csv + text/csv on the presign — RT3).
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Only .csv files can be uploaded for ingestion.');
      return;
    }
    // SCRUM-688: the uploader must pick the report type (the server writes it as the authoritative
    // key segment; the consumer never guesses from the filename). Fail-closed both here and server-side.
    if (reportType.length === 0) {
      setError('Select a report type before uploading.');
      return;
    }
    if (!targetReady) {
      setError('Select a client bank before uploading.');
      return;
    }

    setError(null);
    setUploading(true);
    uploadIngestionFile(file, reportType, isPlatformAdmin ? selectedTenantId : undefined)
      .then((objectKey) => {
        setRecent((prev) => [{ name: file.name, key: objectKey }, ...prev]);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Upload failed. Please try again.');
      })
      .finally(() => {
        setUploading(false);
      });
  };

  return (
    <div className="flex min-h-full flex-col">
      <PageHeader title="Data Ingestion" subtitle="Upload CSV extracts for pipeline processing" />

      <div className="mx-auto w-full max-w-[720px] flex-1 p-8">
        {isPlatformAdmin && (
          <div className="mb-5 flex flex-col gap-1.5">
            <label
              htmlFor="ingestion-tenant"
              className="text-text-3 text-[0.6875rem] font-semibold tracking-wide uppercase"
            >
              Client bank
            </label>
            <select
              id="ingestion-tenant"
              value={selectedTenantId}
              onChange={(e) => {
                setSelectedTenantId(e.target.value);
              }}
              className="border-border-1 bg-bg-3 text-text-0 rounded-tk-md focus:border-brand-primary h-[40px] border px-3 text-[0.8125rem] outline-none"
            >
              <option value="">Select an active client bank…</option>
              {activeTenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.bankName}
                </option>
              ))}
            </select>
            {tenantsError && <p className="text-danger text-xs">{tenantsError}</p>}
          </div>
        )}

        <div className="mb-5 flex flex-col gap-1.5">
          <label
            htmlFor="ingestion-report-type"
            className="text-text-3 text-[0.6875rem] font-semibold tracking-wide uppercase"
          >
            Report type
          </label>
          <select
            id="ingestion-report-type"
            value={reportType}
            onChange={(e) => {
              setReportType(e.target.value);
            }}
            className="border-border-1 bg-bg-3 text-text-0 rounded-tk-md focus:border-brand-primary h-[40px] border px-3 text-[0.8125rem] outline-none"
          >
            <option value="">Select the report type…</option>
            {REPORT_TYPES.map((rt) => (
              <option key={rt.code} value={rt.code}>
                {rt.label}
              </option>
            ))}
          </select>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          aria-label="Choose a CSV file"
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          aria-label="Drop a CSV file here or click to browse"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={`rounded-tk-lg w-full border-2 border-dashed px-8 py-12 text-center transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
            dragOver ? 'border-brand-primary bg-brand-dim' : 'border-border-1 bg-bg-3'
          }`}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            className="text-text-3 mx-auto mb-3 block"
            aria-hidden="true"
          >
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <p className="text-text-2 text-[0.8125rem] font-medium">
            {uploading ? 'Uploading…' : 'Drop a CSV file here or click to browse'}
          </p>
          <p className="text-text-3 mt-1 text-xs">
            CSV only. The file is queued for the next ingestion run.
          </p>
        </button>

        {error && <p className="text-danger mt-3 text-xs">{error}</p>}

        {recent.length > 0 && (
          <div className="mt-6">
            <p className="text-text-3 mb-2 text-[0.6875rem] font-semibold tracking-wide uppercase">
              Uploaded this session
            </p>
            <ul className="flex flex-col gap-2">
              {recent.map((r, i) => (
                <li
                  key={`${r.key}-${String(i)}`}
                  className="bg-bg-2 border-border-0 rounded-tk-md flex items-center justify-between border px-3 py-2"
                >
                  <span className="text-text-0 truncate text-[0.8125rem] font-medium">
                    {r.name}
                  </span>
                  <span className="text-text-3 ml-3 shrink-0 font-mono text-[0.6875rem]">
                    queued
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
